// ---------------------------------------------------------------------------
// Inbox — the caller's own message archive.
// ---------------------------------------------------------------------------
// Defaults to PROJECT messages (the app's world); "Everything" shows the whole
// Odoo notification stream. Unread rows are highlighted; opening a task marks
// its notification read; "Mark all read" clears the counter. Grouped by day.
// Triage keys (direction §5I): j/k (or ↓/↑) walk a cursor row, Enter opens it,
// 'e' marks it read in place; the list auto-loads as the end scrolls into view.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, BellOff, Check, CheckCheck, Clock, Inbox as InboxIcon, X } from 'lucide-react';
import type { InboxItem, InboxPage, InboxScope, InboxSender } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useUiStore } from '@/core/ui/uiStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { prefetchTask, useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useToastStore } from '@/core/ui/toastStore';
import { initials, avatarColor } from '@/core/ui/helpers';
import { Avatar } from '@/core/ui/atoms';
import { Spinner, LoadError } from '@/core/ui/atoms';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';

const PAGE = 30;

/** What's narrowing the list right now. */
interface Filters { unreadOnly: boolean; mentionsOnly: boolean; authorUid: number | null }
const ODOO_URL = 'https://chronicguru.odoo.com';

// Hover-intent prefetch (instant-open layer 3): 100ms of dwell on a task row
// warms the task-detail cache so opening it renders with no spinner. One timer
// is enough — the pointer only hovers one row at a time. Touch-only devices
// never produce a useful mouseenter, so they skip the wiring entirely.
// typeof-guarded: the client unit tests import under NODE, where module-scope
// window access crashes the require before a single test runs.
const HOVERABLE = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;
const hoverStart = (id: number) => {
  if (!HOVERABLE) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => prefetchTask(id), 100);
};
const hoverEnd = () => clearTimeout(hoverTimer);

function stripHtml(html: string): string {
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || '').replace(/\s+/g, ' ').trim();
}
function dayKey(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - that.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
/** Active filters read as ON at a glance — a filter you can't see is a filter you
 *  forget you set, and then the inbox looks empty for no reason. */
function filterChip(active: boolean): string {
  return `h-8 shrink-0 rounded-md border px-2 text-[11px] font-medium transition ${
    active ? 'border-brand/50 bg-brand/15 text-brand' : 'border-line text-muted hover:bg-hover hover:text-ink'
  }`;
}
function fmtTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function Inbox() {
  const close = useUiStore((s) => s.closeInbox);
  useEscapeToClose(() => { if (useTaskDetailStore.getState().openId === null) close(); });
  const openTask = useTaskDetailStore((s) => s.open);
  const selectArea = useAreaStore((s) => s.selectArea);
  const toast = useToastStore((s) => s.show);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<InboxScope>('projects');
  // Narrowing filters. Server-side: a 4,065-message inbox filtered in the browser
  // would only ever filter the 30 rows already fetched and call that the answer.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [mentionsOnly, setMentionsOnly] = useState(false);
  const [authorUid, setAuthorUid] = useState<number | null>(null);
  const [senders, setSenders] = useState<InboxSender[]>([]);
  // The triage cursor — an index into `items` (the flat list the day groups are
  // cut from), moved by j/k, opened by Enter, cleared-in-place by 'e'.
  const [cursor, setCursor] = useState<number | null>(null);
  // v1.79.0: the Monday brief opens in its own little viewer — the list row
  // strips HTML, but a brief IS its links.
  const [brief, setBrief] = useState<InboxItem | null>(null);
  const briefRef = useRef<InboxItem | null>(null);
  briefRef.current = brief;

  // Requests are typed-into (search) and switched (scope), so responses can land
  // out of order. Only the newest request may write state — otherwise a slow
  // earlier response overwrites newer results, or appends onto the wrong list.
  const reqSeq = useRef(0);
  // The args that produced the rows currently on screen — what "load more of
  // THIS list" means, as opposed to whatever the filter inputs say right now
  // (mid-debounce those describe the NEXT list, and appending a new-filter page
  // onto old-filter rows would corrupt both). Written only by the call that
  // actually landed its rows.
  const lastLoadRef = useRef<{ q: string; sc: InboxScope; f: Filters } | null>(null);
  const load = useCallback(async (reset: boolean, q: string, off: number, sc: InboxScope, f: Filters) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const page = await api.get<InboxPage>(
        `/inbox?limit=${PAGE}&offset=${off}&scope=${sc}${q ? `&search=${encodeURIComponent(q)}` : ''}`
        + `${f.unreadOnly ? '&unreadOnly=true' : ''}${f.mentionsOnly ? '&mentionsOnly=true' : ''}`
        + `${f.authorUid ? `&authorUid=${f.authorUid}` : ''}`,
      );
      if (seq !== reqSeq.current) return; // superseded — drop it
      lastLoadRef.current = { q, sc, f };
      setError(null);
      setTotal(page.total);
      setUnread(page.unread);
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
    } catch (e) {
      // NOT "the empty state handles it": telling someone whose token expired that
      // their inbox is empty makes them stop looking for real messages.
      if (seq === reqSeq.current) setError(e instanceof Error ? e.message : 'Could not load your inbox.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { void load(true, search, 0, scope, { unreadOnly, mentionsOnly, authorUid }); }, 250);
    return () => clearTimeout(t);
  }, [search, scope, unreadOnly, mentionsOnly, authorUid, load]);

  // The "From" list follows the scope: who writes to you in Projects is not who
  // writes to you in Everything. Best-effort — a failure just leaves the picker out.
  useEffect(() => {
    let live = true;
    api.get<{ senders: InboxSender[] }>(`/inbox/senders?scope=${scope}`)
      .then((r) => { if (live) setSenders(r.senders); })
      .catch(() => { if (live) setSenders([]); });
    return () => { live = false; };
  }, [scope]);

  // A sender who doesn't exist in the new scope would leave a "From" chip filtering
  // to nothing, with no way to tell why.
  useEffect(() => {
    if (authorUid !== null && senders.length && !senders.some((x) => x.uid === authorUid)) setAuthorUid(null);
  }, [senders, authorUid]);

  /** Clear one row's unread state WITHOUT opening it — shared by the open flow,
   *  the hover ✓, and the 'e' key. Safe to call blind: read rows are a no-op.
   *
   *  Optimistic, but NOT fire-and-forget: if the write fails, put the unread
   *  dot back and say so. Swallowing the error (the old `.catch(()=>undefined)`)
   *  is exactly why "won't mark messages as read" — it looked read, then came
   *  back unread on the next reload with no explanation. */
  const markRead = (it: InboxItem) => {
    if (!it.unread) return;
    setItems((prev) => prev.map((x) => (x.messageId === it.messageId ? { ...x, unread: false } : x)));
    setUnread((u) => Math.max(0, u - 1));
    void api.post('/inbox/read', { messageId: it.messageId }).catch(() => {
      setItems((prev) => prev.map((x) => (x.messageId === it.messageId ? { ...x, unread: true } : x)));
      setUnread((u) => u + 1);
      toast('Could not mark that as read — try again.', 'error');
    });
  };

  const openItem = (it: InboxItem) => {
    markRead(it);
    if (it.model === 'project.task' && it.resId) {
      // Land on the ROOM, not just the building: the conversation scrolls to
      // and flashes this exact message once the panel is up.
      useTaskDetailStore.getState().setFocusMessage(it.messageId ?? null);
      // The inbox STAYS OPEN underneath (v1.85.0, team approvals doc): the
      // panel renders above it, so closing the card reveals the inbox exactly
      // where they left it — instead of stranding them on the project board.
      void openTask(it.resId);
    } else if (it.model === 'project.project' && it.resId) {
      // Project-level message — jump to that project.
      selectArea(it.resId);
      close();
    } else if (it.model === 'cg.brief') {
      // ☀️ Monday brief — sentences with task links; opens in its own viewer.
      setBrief(it);
    } else if (it.model === 'res.partner') {
      // 📣 company announcement — the message IS the content; clicking just
      // marks it read (never dump staff into the Odoo contact form).
    } else if (it.resId) {
      // Non-project records live in Odoo — open there.
      window.open(`${ODOO_URL}/web#id=${it.resId}&model=${it.model}&view_type=form`, '_blank', 'noopener');
    }
  };

  /**
   * Stop a project reaching this inbox — the exit for the complaint that opened the
   * team's feedback doc: "I'm getting emails and notifications… can't access project
   * to clear notification or delete from my inbox." Every other way out lived INSIDE
   * the project, which is the one place the person could not go.
   *
   * The confirm has to cover both cases honestly, because the client cannot know
   * which it is: if you are a member this gives up your access, and if you are not it
   * only silences it. The server reports which happened and the toast says so.
   */
  const leaveProject = async (areaId: number, areaName: string) => {
    const ok = window.confirm(
      `Stop "${areaName}" from sending you anything?

`
      + 'You will stop being notified about its tasks. If you are a member of it, this '
      + 'also gives up your access — you can be added back by its lead.',
    );
    if (!ok) return;
    try {
      const r = await api.post<{ wasMember: boolean; watchersRemoved: number }>(`/areas/${areaId}/leave`, {});
      toast(
        r.wasMember
          ? `Left ${areaName} — you will no longer see it or hear from it.`
          : `${areaName} will no longer notify you${r.watchersRemoved ? ` (${r.watchersRemoved} task subscription${r.watchersRemoved === 1 ? '' : 's'} removed)` : ''}.`,
        'info',
      );
      // Its rows are still on screen and would now be unreachable, so reload the page
      // of results rather than leaving stale items behind.
      void load(true, search, 0, scope, { unreadOnly, mentionsOnly, authorUid });
      void useAreaStore.getState().loadAreas(true);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not leave that project.', 'error');
    }
  };

  const markAllRead = async () => {
    // Await the write BEFORE clearing the badge, and tell the user if it failed.
    // The old bare catch cleared the UI even when the server rejected, so the
    // count silently returned on reload — the reported "Mark all as read doesn't work".
    try {
      await api.post('/inbox/read', { scope });
      setItems((prev) => prev.map((x) => ({ ...x, unread: false })));
      setUnread(0);
    } catch {
      toast('Could not mark all as read — try again.', 'error');
    }
  };

  // Group by day for scannability. Each row keeps its index into the FLAT list —
  // that index is the cursor's coordinate system and the scroll-to hook.
  const groups = useMemo(() => {
    const out: Array<{ day: string; rows: Array<{ it: InboxItem; idx: number }> }> = [];
    items.forEach((it, idx) => {
      const day = dayKey(it.date);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push({ it, idx });
      else out.push({ day, rows: [{ it, idx }] });
    });
    return out;
  }, [items]);

  // --- Triage keyboard (direction §5I). The global keymap in Layout bails while
  // the inbox owns the screen, so the inbox binds its own keys. Registers ONCE;
  // everything the handler needs travels by ref so re-renders don't drop the
  // listener (the Layout keymap lesson).
  const keyCtx = useRef<{
    items: InboxItem[];
    cursor: number | null;
    openItem: (it: InboxItem) => void;
    markRead: (it: InboxItem) => void;
  }>({ items: [], cursor: null, openItem: () => {}, markRead: () => {} });
  useEffect(() => { keyCtx.current = { items, cursor, openItem, markRead }; });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Modified presses belong to the browser.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      // Typing contexts own their keys — 'j' in the search box must type a j.
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      // Anything stacked above the inbox (palette, task panel) owns the keyboard.
      if (useUiStore.getState().searchOpen || useTaskDetailStore.getState().openId !== null) return;
      if (briefRef.current) return; // the brief viewer owns the keyboard while open
      const { items: list, cursor: cur, openItem: open, markRead: mark } = keyCtx.current;
      if (e.key === 'j' || e.key === 'k' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (list.length === 0) return;
        e.preventDefault();
        const down = e.key === 'j' || e.key === 'ArrowDown';
        const next = cur === null ? 0 : Math.min(list.length - 1, Math.max(0, cur + (down ? 1 : -1)));
        setCursor(next);
        // Warm the row like a hover would — Enter usually follows within a beat.
        const it = list[next];
        if (it.model === 'project.task' && it.resId) hoverStart(it.resId);
        return;
      }
      if (e.key === 'Enter') {
        // Enter on a focused control belongs to that control (buttons click on
        // keydown) — the cursor only claims it from the page itself.
        if (t?.closest?.('button, a, select')) return;
        if (cur === null || !list[cur]) return;
        e.preventDefault();
        open(list[cur]);
        return;
      }
      if ((e.key === 'e' || e.key === 'E') && cur !== null && list[cur]) {
        e.preventDefault();
        mark(list[cur]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A new search/scope/filter replaces the list — an index into the old one
  // would silently point at a different message.
  useEffect(() => { setCursor(null); }, [search, scope, unreadOnly, mentionsOnly, authorUid]);
  // The list can also shrink under the cursor (leave-project reload) — clamp,
  // don't leave it dangling past the end.
  useEffect(() => {
    setCursor((c) => (c !== null && c >= items.length ? (items.length ? items.length - 1 : null) : c));
  }, [items.length]);
  // Arrowing past the fold must scroll, or the cursor vanishes off-screen.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (cursor === null) return;
    scrollerRef.current?.querySelector<HTMLElement>(`[data-row="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  // --- Infinite scroll (direction §5I): a sentinel at the list's end loads the
  // next page as it approaches the viewport. Recreated whenever the list grows —
  // a fresh observe() reports current intersection immediately, so a short page
  // that leaves the sentinel still on screen chains straight into the next load
  // instead of stalling until the user scrolls again. The Load-more button
  // survives as the fallback where IntersectionObserver doesn't exist.
  const hasIO = typeof IntersectionObserver !== 'undefined';
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMore = () => {
    const last = lastLoadRef.current;
    if (!last || loading || items.length === 0 || items.length >= total) return;
    void load(false, last.q, items.length, last.sc, last.f);
  };
  const loadMoreRef = useRef(loadMore);
  useEffect(() => { loadMoreRef.current = loadMore; });
  useEffect(() => {
    if (!hasIO) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((x) => x.isIntersecting)) loadMoreRef.current(); },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasIO, items.length, total]);

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-page animate-fade-in">
      {/* The way OUT is the first thing in the header and never shrinks. The Inbox
          is a full-screen overlay (not a route), so browser Back can't close it —
          an always-visible, labelled control is the only reliable exit. Everything
          else may compress; the search box absorbs the squeeze (min-w-0) so the
          exit can never be pushed off a narrow screen. */}
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3 sm:gap-3 sm:px-4">
        <button
          onClick={close}
          title="Back (Esc)"
          className="flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-xs font-medium text-muted transition hover:bg-hover hover:text-ink"
        >
          <Icon i={ArrowLeft} size={14} /> Back
        </button>
        <span className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-ink sm:inline-flex"><Icon i={InboxIcon} size={14} /> Inbox</span>
        <nav className="flex shrink-0 items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5">
          {(['projects', 'all'] as InboxScope[]).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`rounded px-2.5 py-1 text-xs font-medium transition ${scope === s ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}
            >
              {s === 'projects' ? 'Projects' : 'Everything'}
            </button>
          ))}
        </nav>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="h-8 w-24 min-w-[6rem] flex-1 rounded-md border border-line bg-canvas px-3 text-xs text-ink outline-none placeholder:text-faint focus:border-brand sm:max-w-[16rem]"
        />
        {/* The filters that actually shrink a 4,000-message inbox. Each one is a
            server-side narrow, so the count beside them describes the whole set,
            not the 30 rows already on screen. */}
        <button
          onClick={() => setUnreadOnly((v) => !v)}
          aria-pressed={unreadOnly}
          title="Only what you haven't opened"
          className={filterChip(unreadOnly)}
        >
          Unread
        </button>
        <button
          onClick={() => setMentionsOnly((v) => !v)}
          aria-pressed={mentionsOnly}
          title="Only messages that say your name"
          className={filterChip(mentionsOnly)}
        >
          @ me
        </button>
        {senders.length > 0 && (
          <select
            value={authorUid ?? ''}
            onChange={(e) => setAuthorUid(e.target.value ? Number(e.target.value) : null)}
            title="Only messages from one person"
            className={`h-8 shrink-0 rounded-md border bg-canvas px-1.5 text-[11px] outline-none focus:border-brand ${authorUid ? 'border-brand/50 text-brand' : 'border-line text-muted'}`}
          >
            <option value="">Anyone</option>
            {senders.filter((x) => x.uid !== null).map((x) => (
              <option key={x.partnerId} value={x.uid ?? ''}>{x.name} ({x.count})</option>
            ))}
          </select>
        )}
        {(unreadOnly || mentionsOnly || authorUid !== null || search) && (
          <button
            onClick={() => { setUnreadOnly(false); setMentionsOnly(false); setAuthorUid(null); setSearch(''); }}
            className="shrink-0 rounded-md border border-line px-1.5 py-0.5 text-[11px] text-muted transition hover:bg-hover hover:text-ink"
            title="Clear every filter"
          >
            Clear
          </button>
        )}
        {unread > 0 && (
          <>
            <span className="shrink-0 rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-medium text-brand">{unread} unread</span>
            {/* Phones get a compact control — hiding this entirely left mobile users
                with no way to clear their unread badge at all. */}
            <button onClick={() => void markAllRead()} title="Mark all read" className="shrink-0 rounded-md border border-line px-1.5 py-0.5 text-muted transition hover:bg-hover hover:text-ink sm:hidden">
              <Icon i={CheckCheck} size={14} />
            </button>
            <button onClick={() => void markAllRead()} className="hidden shrink-0 text-[11px] text-muted underline-offset-2 hover:text-ink hover:underline sm:inline">
              Mark all read
            </button>
          </>
        )}
        <span className="ml-auto hidden shrink-0 whitespace-nowrap text-xs text-faint sm:inline">{total.toLocaleString()} message{total === 1 ? '' : 's'}</span>
        <button onClick={close} className="shrink-0 rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close (Esc)"><Icon i={X} size={14} /></button>
      </header>

      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-4">
          {error && items.length === 0 ? (
            <LoadError message={error} onRetry={() => void load(true, search, 0, scope, { unreadOnly, mentionsOnly, authorUid })} />
          ) : loading && items.length === 0 ? (
            <div className="py-8"><Spinner label="Loading your inbox…" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-faint">
              {search ? 'No messages match your search.' : scope === 'projects'
                ? 'No project messages yet — comments and @mentions on your tasks will land here.'
                : 'No messages yet.'}
            </div>
          ) : (
            groups.map((g) => (
              <section key={g.day} className="mb-5">
                <h3 className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-faint">{g.day}</h3>
                <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                  {g.rows.map(({ it, idx }) => (
                    <div
                      key={it.messageId}
                      data-row={idx}
                      // Only task rows have a detail panel to warm — everything else
                      // opens a project, Odoo, or nothing.
                      onMouseEnter={() => { if (it.model === 'project.task' && it.resId) hoverStart(it.resId); }}
                      onMouseLeave={hoverEnd}
                      // The cursor row wears the board card's focus-ring look, drawn
                      // inset so the group container's overflow-hidden can't clip it.
                      className={`group/row relative border-b border-line last:border-0 ${idx === cursor ? 'bg-hover ring-2 ring-inset ring-brand' : ''}`}
                    >
                    <button
                      onClick={() => openItem(it)}
                      className={`flex w-full items-start gap-3 px-3 py-2.5 pr-14 text-left hover:bg-hover ${it.unread ? 'bg-brand/5' : ''}`}
                    >
                      <span className="relative mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center">
                        <Avatar person={{ id: it.authorId ?? -1, name: it.authorName, avatarUrl: null }} size={28} />
                        {it.unread && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-canvas bg-brand" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className={`truncate text-[13px] ${it.unread ? 'font-semibold' : 'font-medium'} text-ink`}>{it.authorName}</span>
                          {it.recordName && <span className="truncate text-[11px] text-brand">{it.recordName}</span>}
                          {it.model === 'res.partner' ? (
                            <span className="shrink-0 rounded bg-brand/15 px-1 py-px text-[9px] uppercase tracking-wide text-brand" title="Company-wide announcement">announcement</span>
                          ) : it.model === 'cg.brief' ? (
                            <span className="shrink-0 rounded bg-success-soft px-1 py-px text-[9px] uppercase tracking-wide text-success" title="Your week, in plain sentences — click to read">monday brief</span>
                          ) : it.model !== 'project.task' && it.model !== 'project.project' ? (
                            <span className="shrink-0 rounded bg-hover px-1 py-px text-[9px] uppercase tracking-wide text-faint" title={`Lives in Odoo (${it.model}) — opens in a new tab`}>odoo ↗</span>
                          ) : null}
                          <span className="ml-auto shrink-0 text-[10px] text-faint">{fmtTime(it.date)}</span>
                        </div>
                        <div className={`mt-0.5 line-clamp-2 text-[12px] ${it.unread ? 'text-ink' : 'text-muted'}`}>{stripHtml(it.body)}</div>
                      </div>
                    </button>
                    {/* Siblings, not nested: a button inside a button is invalid HTML
                        and the inner one's click would not survive. Revealed on hover —
                        and on focus-within, so tabbing into the row exposes them to the
                        keyboard (a display:none button can never receive focus, so the
                        old per-button focus-visible reveal was unreachable). */}
                    {(it.unread || (it.areaId && it.areaName)) && (
                      <div className="absolute right-1.5 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 group-focus-within/row:flex group-hover/row:flex">
                        {it.unread && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markRead(it); }}
                            title="Mark read without opening (e)"
                            aria-label="Mark read without opening"
                            className="rounded p-1 text-faint transition hover:bg-hover hover:text-success"
                          ><Icon i={Check} size={14} /></button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // "Not now, but don't let me forget": vanishes until
                            // tomorrow 9am, then comes back on its own.
                            setItems((prev) => prev.filter((x) => x.messageId !== it.messageId));
                            if (it.unread) setUnread((u) => Math.max(0, u - 1));
                            void api.post('/inbox/snooze', { messageId: it.messageId })
                              .then(() => toast('Snoozed — back tomorrow morning.', 'info'))
                              .catch(() => toast('Could not snooze that.', 'error'));
                          }}
                          title="Snooze until tomorrow morning"
                          aria-label="Snooze until tomorrow morning"
                          className="rounded p-1 text-faint transition hover:bg-hover hover:text-warn"
                        ><Icon i={Clock} size={14} /></button>
                        {it.areaId && it.areaName && (
                          <button
                            onClick={(e) => { e.stopPropagation(); void leaveProject(it.areaId as number, it.areaName as string); }}
                            title={`Stop "${it.areaName}" from notifying you`}
                            aria-label={`Stop ${it.areaName} from notifying you`}
                            className="rounded p-1 text-faint transition hover:bg-hover hover:text-danger"
                          ><Icon i={BellOff} size={14} /></button>
                        )}
                      </div>
                    )}
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}

          {items.length > 0 && items.length < total && (hasIO ? (
            <div ref={sentinelRef} className="my-2 flex h-12 items-center justify-center text-xs text-faint">
              {loading ? 'Loading…' : ''}
            </div>
          ) : (
            <div className="mt-2 flex justify-center pb-6">
              <button
                onClick={loadMore}
                disabled={loading}
                className="rounded-md border border-line px-4 py-1.5 text-xs text-muted hover:bg-hover disabled:opacity-50"
              >
                {loading ? 'Loading…' : `Load more (${(total - items.length).toLocaleString()} left)`}
              </button>
            </div>
          ))}
        </div>
      </div>
      {brief && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setBrief(null)}>
          <div
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl border border-line bg-canvas p-5 shadow-2xl"
            onClick={(e) => {
              e.stopPropagation();
              // A task link inside the brief navigates in place (the router owns
              // #/t/…) — close the viewer AND the inbox so the card is visible.
              const a = (e.target as HTMLElement).closest('a[href^="#/t/"]');
              if (a) { setBrief(null); close(); }
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">{fmtTime(brief.date)}</span>
              <button onClick={() => setBrief(null)} className="rounded p-1 text-faint hover:text-ink" title="Close"><Icon i={X} size={14} /></button>
            </div>
            <div className="cg-prose text-[13.5px] leading-relaxed [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: brief.body }} />
          </div>
        </div>
      )}
    </div>
  );
}
